import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import TopBar from './TopBar';
import Sidebar from './Sidebar';
import ContentSkeleton from '../UI/ContentSkeleton';
import { prefetchCommonRoutes } from '../../routes/prefetch';

const Layout = () => {
  const theme = useTheme();
  // Phones and small tablets get the overlay drawer; anything wider keeps the
  // persistent drawer, which the hamburger now collapses/expands.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Warm the most visited sections once the browser is idle, so the first click on
  // them does not have to wait for a chunk over the wire.
  useEffect(() => {
    prefetchCommonRoutes();
  }, []);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
      return;
    }
    setSidebarOpen((prev) => !prev);
  };

  // Leaving the overlay drawer mounted open would block the persistent sidebar
  // after the viewport grows.
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const drawerOpen = isMobile ? mobileOpen : sidebarOpen;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', minWidth: 0, backgroundColor: 'background.default' }}>
      <TopBar onMenuClick={handleDrawerToggle} drawerOpen={drawerOpen} />
      <Sidebar open={drawerOpen} onToggle={handleDrawerToggle} isMobile={isMobile} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // `minWidth: 0` lets the main column shrink below its content's
          // intrinsic width (Chrome/Edge otherwise let a wide table stretch the
          // whole document); `overflowX: hidden` is the last-resort guardrail.
          minWidth: 0,
          p: { xs: 1.5, sm: 2, md: 3 },
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
          overflowX: 'hidden',
        }}
      >
        <Toolbar />
        {/* Suspense lives here (not around the routes) so a lazily loaded section
            swaps only this area: TopBar and Sidebar stay mounted, the page does not
            scroll to the top and no full-screen overlay appears. */}
        <React.Suspense fallback={<ContentSkeleton />}>
          <Outlet />
        </React.Suspense>
      </Box>
    </Box>
  );
};

export default Layout;